import { getSupabase } from './supabase';
import { processReferralRewardIfEligible } from './supabaseLoyalty';
import { getReferralRewardSettings, DEFAULT_REFERRAL_SETTINGS } from './supabaseReferralSettings';

export const REFERRAL_REWARD_REFERRER = DEFAULT_REFERRAL_SETTINGS.referrer_reward;
export const REFERRAL_REWARD_NEW_MEMBER = DEFAULT_REFERRAL_SETTINGS.referred_reward;

export interface ReferredFriend {
  id: string;
  namaLengkap: string;
  createdAt: string;
  isRewarded: boolean;
}

export interface CustomerReferralStats {
  referralCode: string;
  referredFriends: ReferredFriend[];
  totalReferred: number;
  successfulReferrals: number;
  pendingReferrals: number;
  totalPointsEarned: number;
  rewardTransactionCount: number;
  isFromTransactionLedger: boolean;
}

/**
 * Generate a clean, human-friendly 6-character referral code (Format: LETXXXXXX)
 * Uses uppercase letters and numbers avoiding easily confusable characters (0, O, 1, I).
 */
export function generateReferralCode(): string {
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  let result = 'LET';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Validate a referral code against Supabase database (strictly once during registration).
 * Prevents self-referral.
 */
export async function validateReferralCode(
  rawCode: string,
  candidatePhone?: string
): Promise<{ isValid: boolean; referrerId?: string; referrerName?: string; error?: string }> {
  const cleanCode = (rawCode || '').trim().toUpperCase();
  if (!cleanCode) {
    return { isValid: false, error: 'Kode referral kosong.' };
  }

  try {
    const client = getSupabase();
    const { data, error } = await client
      .from('customers')
      .select('id, nama_lengkap, nomor_hp, referral_code')
      .ilike('referral_code', cleanCode)
      .maybeSingle();

    if (error) {
      console.warn('[validateReferralCode] Database lookup notice:', error.message);
      return { isValid: false, error: 'Gagal memverifikasi kode referral.' };
    }

    if (!data) {
      return { isValid: false, error: 'Kode referral tidak ditemukan atau tidak valid.' };
    }

    // Prevent self-referral if phone is provided
    if (candidatePhone) {
      const cleanCandidate = candidatePhone.replace(/[^0-9]/g, '');
      const cleanOwner = (data.nomor_hp || '').replace(/[^0-9]/g, '');
      if (cleanCandidate && cleanOwner && (cleanCandidate === cleanOwner || cleanCandidate.endsWith(cleanOwner) || cleanOwner.endsWith(cleanCandidate))) {
        return { isValid: false, error: 'Anda tidak dapat menggunakan kode referral Anda sendiri.' };
      }
    }

    return {
      isValid: true,
      referrerId: data.id,
      referrerName: data.nama_lengkap || 'Member Leton',
    };
  } catch (err: any) {
    console.warn('[validateReferralCode] Exception:', err);
    return { isValid: false, error: 'Terjadi kendala saat memeriksa kode referral.' };
  }
}

/**
 * Fetch customer referral stats, verified successful referrals, and actual reward points earned
 * from authoritative database tables (public.customers & public.loyalty_transactions).
 */
export async function fetchCustomerReferralStats(
  customerId: string,
  customerReferralCode?: string
): Promise<CustomerReferralStats> {
  const defaultResult: CustomerReferralStats = {
    referralCode: customerReferralCode || '',
    referredFriends: [],
    totalReferred: 0,
    successfulReferrals: 0,
    pendingReferrals: 0,
    totalPointsEarned: 0,
    rewardTransactionCount: 0,
    isFromTransactionLedger: true,
  };

  if (!customerId) return defaultResult;

  try {
    const client = getSupabase();

    // 1. Fetch referral code directly from database (source of truth)
    let activeCode = '';
    const { data: custData } = await client
      .from('customers')
      .select('referral_code')
      .eq('id', customerId)
      .maybeSingle();

    if (custData && custData.referral_code) {
      activeCode = custData.referral_code;
    } else if (customerReferralCode) {
      activeCode = customerReferralCode;
    }

    // If still missing (legacy customer without referral_code in DB), generate one and save to DB
    if (!activeCode) {
      activeCode = generateReferralCode();
      try {
        const adminClient = getSupabase('super_admin');
        const { error: updateErr } = await adminClient
          .from('customers')
          .update({ referral_code: activeCode })
          .eq('id', customerId);

        if (!updateErr && typeof window !== 'undefined') {
          const cachedProfile = localStorage.getItem('leton_customer_profile');
          if (cachedProfile) {
            try {
              const parsedProfile = JSON.parse(cachedProfile);
              parsedProfile.referralCode = activeCode;
              localStorage.setItem('leton_customer_profile', JSON.stringify(parsedProfile));
            } catch {}
          }
        }
      } catch (err) {
        console.warn('[fetchCustomerReferralStats] DB update notice:', err);
      }
    }

    // 2. Fetch referred friends from public.customers (referred_by = customerId), deduplicated by customer id
    const { data: friendsData, error: friendsErr } = await client
      .from('customers')
      .select('id, nama_lengkap, created_at, referral_rewarded')
      .eq('referred_by', customerId)
      .neq('id', customerId)
      .order('created_at', { ascending: false });

    const uniqueFriendsMap = new Map<string, ReferredFriend>();
    if (!friendsErr && Array.isArray(friendsData)) {
      for (const f of friendsData) {
        if (f && f.id && !uniqueFriendsMap.has(f.id)) {
          uniqueFriendsMap.set(f.id, {
            id: f.id,
            namaLengkap: f.nama_lengkap || 'Member Baru',
            createdAt: f.created_at || new Date().toISOString(),
            isRewarded: Boolean(f.referral_rewarded),
          });
        }
      }
    }

    const friends = Array.from(uniqueFriendsMap.values());
    const rewardedFriendsCount = friends.filter((f) => f.isRewarded).length;
    const pendingFriendsCount = friends.filter((f) => !f.isRewarded).length;

    // 3. Fetch actual referral reward transactions from public.loyalty_transactions
    let ledgerPointsTotal = 0;
    let ledgerTxCount = 0;
    let ledgerQuerySucceeded = false;

    try {
      const { data: txRows, error: txErr } = await client
        .from('loyalty_transactions')
        .select('id, points, reason, transaction_type, reference_order_id, created_at')
        .eq('customer_id', customerId)
        .eq('transaction_type', 'EARN')
        .ilike('reason', '%Referral Reward%');

      if (!txErr && Array.isArray(txRows)) {
        ledgerQuerySucceeded = true;
        const seenTxIds = new Set<string>();
        const seenOrderRefs = new Set<string>();

        for (const row of txRows) {
          if (!row || !row.id) continue;
          const reasonLower = String(row.reason || '').toLowerCase();
          // Exclude bonus received as a referred new member ("Referral Reward - Referred Member")
          if (reasonLower.includes('referred member')) continue;

          const pts = Number(row.points || 0);
          if (pts <= 0) continue;

          // Deduplicate by transaction ID and reference_order_id (if present and not generic)
          if (seenTxIds.has(row.id)) continue;
          const orderRef = row.reference_order_id ? String(row.reference_order_id).trim() : '';
          if (orderRef && orderRef !== 'FIRST-ORDER' && seenOrderRefs.has(orderRef)) {
            continue;
          }

          seenTxIds.add(row.id);
          if (orderRef && orderRef !== 'FIRST-ORDER') {
            seenOrderRefs.add(orderRef);
          }

          ledgerPointsTotal += pts;
          ledgerTxCount += 1;
        }
      }
    } catch (txQueryErr) {
      console.warn('[fetchCustomerReferralStats] loyalty_transactions query notice:', txQueryErr);
    }

    // Verified successful referrals count: strictly those who met first valid order requirement
    const verifiedSuccessfulCount = Math.max(rewardedFriendsCount, ledgerTxCount);

    // Total points earned: prioritize actual transaction ledger sum; fallback to settings only if ledger wasn't readable
    let finalPointsEarned = ledgerPointsTotal;
    let isFromLedger = true;

    if (ledgerTxCount === 0 && rewardedFriendsCount > 0 && !ledgerQuerySucceeded) {
      const { settings } = await getReferralRewardSettings();
      finalPointsEarned = rewardedFriendsCount * Number(settings.referrer_reward || 0);
      isFromLedger = false;
    }

    return {
      referralCode: activeCode,
      referredFriends: friends,
      totalReferred: friends.length,
      successfulReferrals: verifiedSuccessfulCount,
      pendingReferrals: pendingFriendsCount,
      totalPointsEarned: finalPointsEarned,
      rewardTransactionCount: ledgerTxCount,
      isFromTransactionLedger: isFromLedger,
    };
  } catch (err) {
    console.warn('[fetchCustomerReferralStats] Error:', err);
    return defaultResult;
  }
}

/**
 * Process referral reward when a newly referred customer completes their first valid order.
 * Delegates to canonical processReferralRewardIfEligible for unified idempotency.
 */
export async function processReferralRewardOnFirstValidOrder(
  newCustomerId: string,
  orderId: string
): Promise<{ awarded: boolean; referrerId?: string; error?: string }> {
  if (!newCustomerId) return { awarded: false };

  try {
    const success = await processReferralRewardIfEligible(newCustomerId, orderId);
    return { awarded: success };
  } catch (err: any) {
    console.error('[processReferralRewardOnFirstValidOrder] Exception:', err);
    return { awarded: false, error: err?.message || 'Internal referral exception' };
  }
}

/**
 * Extract referral code safely from window.location.search or window.location.hash
 * (supports ?ref=, ?referral=, ?referralCode=, including hash patterns like #/?ref=KODE).
 * Persists valid code temporarily in sessionStorage ('leton_ref_code').
 */
export function extractAndPersistReferralCode(): string {
  if (typeof window === 'undefined') return '';
  try {
    const searchParams = new URLSearchParams(window.location.search);
    let raw = searchParams.get('ref') || searchParams.get('referral') || searchParams.get('referralCode');

    if (!raw && window.location.hash) {
      const hash = window.location.hash;
      if (hash.includes('?')) {
        const hashQuery = hash.split('?')[1] || '';
        const hashParams = new URLSearchParams(hashQuery);
        raw = hashParams.get('ref') || hashParams.get('referral') || hashParams.get('referralCode');
      }
    }

    if (raw) {
      const clean = raw.trim().toUpperCase();
      if (clean && clean.length <= 25) {
        sessionStorage.setItem('leton_ref_code', clean);
        return clean;
      }
    }

    const saved = sessionStorage.getItem('leton_ref_code');
    if (saved) {
      return saved.trim().toUpperCase();
    }
  } catch (err) {
    console.warn('[extractAndPersistReferralCode] Notice:', err);
  }
  return '';
}

export function getStoredReferralCode(): string {
  if (typeof window === 'undefined') return '';
  try {
    const saved = sessionStorage.getItem('leton_ref_code');
    return saved ? saved.trim().toUpperCase() : '';
  } catch {
    return '';
  }
}

export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem('leton_ref_code');
  } catch {}
}

